package com.alpha.showcase.common.ai

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performInTransactionSuspending
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Boolean
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.Map
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import okio.BufferedSink
import okio.BufferedSource

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class ImageSummaryDao_Impl(
  __db: RoomDatabase,
) : ImageSummaryDao() {
  private val __db: RoomDatabase

  private val __insertAdapterOfSummaryContentRow: EntityInsertAdapter<SummaryContentRow>

  private val __insertAdapterOfSummaryRevisionRow: EntityInsertAdapter<SummaryRevisionRow>

  private val __insertAdapterOfSummaryHeadRow: EntityInsertAdapter<SummaryHeadRow>

  private val __insertAdapterOfSummaryReferenceRow: EntityInsertAdapter<SummaryReferenceRow>
  init {
    this.__db = __db
    this.__insertAdapterOfSummaryContentRow = object : EntityInsertAdapter<SummaryContentRow>() {
      protected override fun createQuery(): String = "INSERT OR IGNORE INTO `summary_content` (`contentId`,`byteCount`) VALUES (?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: SummaryContentRow) {
        statement.bindText(1, entity.contentId)
        val _tmpByteCount: Long? = entity.byteCount
        if (_tmpByteCount == null) {
          statement.bindNull(2)
        } else {
          statement.bindLong(2, _tmpByteCount)
        }
      }
    }
    this.__insertAdapterOfSummaryRevisionRow = object : EntityInsertAdapter<SummaryRevisionRow>() {
      protected override fun createQuery(): String = "INSERT OR ABORT INTO `summary_revision` (`revisionId`,`contentId`,`body`) VALUES (?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: SummaryRevisionRow) {
        statement.bindText(1, entity.revisionId)
        statement.bindText(2, entity.contentId)
        statement.bindText(3, entity.body)
      }
    }
    this.__insertAdapterOfSummaryHeadRow = object : EntityInsertAdapter<SummaryHeadRow>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `summary_head` (`contentId`,`revisionId`) VALUES (?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: SummaryHeadRow) {
        statement.bindText(1, entity.contentId)
        statement.bindText(2, entity.revisionId)
      }
    }
    this.__insertAdapterOfSummaryReferenceRow = object : EntityInsertAdapter<SummaryReferenceRow>() {
      protected override fun createQuery(): String = "INSERT OR IGNORE INTO `summary_file_reference` (`contentId`,`referenceId`,`body`) VALUES (?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: SummaryReferenceRow) {
        statement.bindText(1, entity.contentId)
        statement.bindText(2, entity.referenceId)
        statement.bindText(3, entity.body)
      }
    }
  }

  public override suspend fun insertContent(row: SummaryContentRow): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfSummaryContentRow.insert(_connection, row)
  }

  public override suspend fun insertRevision(row: SummaryRevisionRow): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfSummaryRevisionRow.insert(_connection, row)
  }

  public override suspend fun select(row: SummaryHeadRow): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfSummaryHeadRow.insert(_connection, row)
  }

  public override suspend fun insertReference(row: SummaryReferenceRow): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfSummaryReferenceRow.insert(_connection, row)
  }

  public override suspend fun rememberReference(reference: SummaryFileReference): Unit = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.rememberReference(reference)
  }

  public override suspend fun find(identity: ImageContentIdentity): AiSummaryContent? = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.find(identity)
  }

  public override suspend fun save(
    identity: ImageContentIdentity,
    content: AiSummaryContent,
    language: String,
  ): Unit = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.save(identity, content, language)
  }

  public override suspend fun migrate(
    identity: ImageContentIdentity,
    records: Map<String, List<AiSummaryContent>>,
    language: String,
  ): Unit = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.migrate(identity, records, language)
  }

  public override suspend fun importArchive(source: BufferedSource): SummaryImportResult = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.importArchive(source)
  }

  public override suspend fun exportArchive(sink: BufferedSink, includeDiagnostics: Boolean): SummaryArchiveCounts = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.exportArchive(sink, includeDiagnostics)
  }

  public override suspend fun exportCsv(sink: BufferedSink): SummaryArchiveCounts = performInTransactionSuspending(__db) {
    super@ImageSummaryDao_Impl.exportCsv(sink)
  }

  public override suspend fun content(id: String): SummaryContentRow? {
    val _sql: String = "SELECT * FROM summary_content WHERE contentId = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfByteCount: Int = getColumnIndexOrThrow(_stmt, "byteCount")
        val _result: SummaryContentRow?
        if (_stmt.step()) {
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpByteCount: Long?
          if (_stmt.isNull(_columnIndexOfByteCount)) {
            _tmpByteCount = null
          } else {
            _tmpByteCount = _stmt.getLong(_columnIndexOfByteCount)
          }
          _result = SummaryContentRow(_tmpContentId,_tmpByteCount)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun revision(id: String): SummaryRevisionRow? {
    val _sql: String = "SELECT * FROM summary_revision WHERE revisionId = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfRevisionId: Int = getColumnIndexOrThrow(_stmt, "revisionId")
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfBody: Int = getColumnIndexOrThrow(_stmt, "body")
        val _result: SummaryRevisionRow?
        if (_stmt.step()) {
          val _tmpRevisionId: String
          _tmpRevisionId = _stmt.getText(_columnIndexOfRevisionId)
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpBody: String
          _tmpBody = _stmt.getText(_columnIndexOfBody)
          _result = SummaryRevisionRow(_tmpRevisionId,_tmpContentId,_tmpBody)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun head(id: String): SummaryHeadRow? {
    val _sql: String = "SELECT * FROM summary_head WHERE contentId = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfRevisionId: Int = getColumnIndexOrThrow(_stmt, "revisionId")
        val _result: SummaryHeadRow?
        if (_stmt.step()) {
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpRevisionId: String
          _tmpRevisionId = _stmt.getText(_columnIndexOfRevisionId)
          _result = SummaryHeadRow(_tmpContentId,_tmpRevisionId)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countRevisions(): Long {
    val _sql: String = "SELECT COUNT(*) FROM summary_revision"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long
        if (_stmt.step()) {
          val _tmp: Long
          _tmp = _stmt.getLong(0)
          _result = _tmp
        } else {
          _result = 0L
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countSummaries(): Long {
    val _sql: String = "SELECT COUNT(DISTINCT contentId) FROM summary_revision"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long
        if (_stmt.step()) {
          val _tmp: Long
          _tmp = _stmt.getLong(0)
          _result = _tmp
        } else {
          _result = 0L
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countHeads(): Long {
    val _sql: String = "SELECT COUNT(*) FROM summary_head"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long
        if (_stmt.step()) {
          val _tmp: Long
          _tmp = _stmt.getLong(0)
          _result = _tmp
        } else {
          _result = 0L
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun countReferences(): Long {
    val _sql: String = "SELECT COUNT(*) FROM summary_file_reference"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long
        if (_stmt.step()) {
          val _tmp: Long
          _tmp = _stmt.getLong(0)
          _result = _tmp
        } else {
          _result = 0L
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun revisions(after: String): List<SummaryRevisionRow> {
    val _sql: String = "SELECT * FROM summary_revision WHERE revisionId > ? ORDER BY revisionId LIMIT 16"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, after)
        val _columnIndexOfRevisionId: Int = getColumnIndexOrThrow(_stmt, "revisionId")
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfBody: Int = getColumnIndexOrThrow(_stmt, "body")
        val _result: MutableList<SummaryRevisionRow> = mutableListOf()
        while (_stmt.step()) {
          val _item: SummaryRevisionRow
          val _tmpRevisionId: String
          _tmpRevisionId = _stmt.getText(_columnIndexOfRevisionId)
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpBody: String
          _tmpBody = _stmt.getText(_columnIndexOfBody)
          _item = SummaryRevisionRow(_tmpRevisionId,_tmpContentId,_tmpBody)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun heads(after: String): List<SummaryHeadRow> {
    val _sql: String = "SELECT * FROM summary_head WHERE contentId > ? ORDER BY contentId LIMIT 100"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, after)
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfRevisionId: Int = getColumnIndexOrThrow(_stmt, "revisionId")
        val _result: MutableList<SummaryHeadRow> = mutableListOf()
        while (_stmt.step()) {
          val _item: SummaryHeadRow
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpRevisionId: String
          _tmpRevisionId = _stmt.getText(_columnIndexOfRevisionId)
          _item = SummaryHeadRow(_tmpContentId,_tmpRevisionId)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun references(content: String, reference: String): List<SummaryReferenceRow> {
    val _sql: String = "SELECT * FROM summary_file_reference WHERE contentId > ? OR (contentId = ? AND referenceId > ?) ORDER BY contentId, referenceId LIMIT 100"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, content)
        _argIndex = 2
        _stmt.bindText(_argIndex, content)
        _argIndex = 3
        _stmt.bindText(_argIndex, reference)
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfReferenceId: Int = getColumnIndexOrThrow(_stmt, "referenceId")
        val _columnIndexOfBody: Int = getColumnIndexOrThrow(_stmt, "body")
        val _result: MutableList<SummaryReferenceRow> = mutableListOf()
        while (_stmt.step()) {
          val _item: SummaryReferenceRow
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpReferenceId: String
          _tmpReferenceId = _stmt.getText(_columnIndexOfReferenceId)
          val _tmpBody: String
          _tmpBody = _stmt.getText(_columnIndexOfBody)
          _item = SummaryReferenceRow(_tmpContentId,_tmpReferenceId,_tmpBody)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun referencesForContent(content: String, after: String): List<SummaryReferenceRow> {
    val _sql: String = "SELECT * FROM summary_file_reference WHERE contentId = ? AND referenceId > ? ORDER BY referenceId LIMIT 100"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, content)
        _argIndex = 2
        _stmt.bindText(_argIndex, after)
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfReferenceId: Int = getColumnIndexOrThrow(_stmt, "referenceId")
        val _columnIndexOfBody: Int = getColumnIndexOrThrow(_stmt, "body")
        val _result: MutableList<SummaryReferenceRow> = mutableListOf()
        while (_stmt.step()) {
          val _item: SummaryReferenceRow
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpReferenceId: String
          _tmpReferenceId = _stmt.getText(_columnIndexOfReferenceId)
          val _tmpBody: String
          _tmpBody = _stmt.getText(_columnIndexOfBody)
          _item = SummaryReferenceRow(_tmpContentId,_tmpReferenceId,_tmpBody)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun reference(content: String, reference: String): SummaryReferenceRow? {
    val _sql: String = "SELECT * FROM summary_file_reference WHERE contentId = ? AND referenceId = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, content)
        _argIndex = 2
        _stmt.bindText(_argIndex, reference)
        val _columnIndexOfContentId: Int = getColumnIndexOrThrow(_stmt, "contentId")
        val _columnIndexOfReferenceId: Int = getColumnIndexOrThrow(_stmt, "referenceId")
        val _columnIndexOfBody: Int = getColumnIndexOrThrow(_stmt, "body")
        val _result: SummaryReferenceRow?
        if (_stmt.step()) {
          val _tmpContentId: String
          _tmpContentId = _stmt.getText(_columnIndexOfContentId)
          val _tmpReferenceId: String
          _tmpReferenceId = _stmt.getText(_columnIndexOfReferenceId)
          val _tmpBody: String
          _tmpBody = _stmt.getText(_columnIndexOfBody)
          _result = SummaryReferenceRow(_tmpContentId,_tmpReferenceId,_tmpBody)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun updateReferenceBody(
    content: String,
    reference: String,
    body: String,
  ) {
    val _sql: String = "UPDATE summary_file_reference SET body = ? WHERE contentId = ? AND referenceId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, body)
        _argIndex = 2
        _stmt.bindText(_argIndex, content)
        _argIndex = 3
        _stmt.bindText(_argIndex, reference)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun completeLength(id: String, size: Long) {
    val _sql: String = "UPDATE summary_content SET byteCount = ? WHERE contentId = ? AND byteCount IS NULL"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, size)
        _argIndex = 2
        _stmt.bindText(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
